import { NgTemplateOutlet } from '@angular/common';
import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TimeUtils } from '@freelancer/time-utils';
import { ButtonComponent } from '@freelancer/ui/button';
import { CardComponent, type CardBorderRadius } from '@freelancer/ui/card';
import { trackByValue } from '@freelancer/ui/helpers';
import { IconModule, IconColor, IconSize } from '@freelancer/ui/icon';
import {
  InputBackgroundColor,
  InputComponent,
  InputIconStart,
  InputSize,
  InputType,
} from '@freelancer/ui/input';
import { ListItemComponent } from '@freelancer/ui/list-item';
import { ScrollableContentComponent } from '@freelancer/ui/scrollable-content';
import type { Subscription } from 'rxjs';
import { SearchExpandFromDirection } from './search.types';
import type { GroupedSearchResults, SearchItem } from './search.types';
import { Focus } from '../focus/focus.service';
import { ListComponent } from '../list/list.component';

@Component({
  standalone: true,
  selector: 'fl-search',
  imports: [
    ButtonComponent,
    CardComponent,
    IconModule,
    InputComponent,
    ListComponent,
    ListItemComponent,
    NgTemplateOutlet,
    ScrollableContentComponent,
  ],
  template: `
    <fl-input
      #inputComponent
      class="Input"
      [backgroundColor]="backgroundColor"
      [borderless]="borderless"
      [borderlessMobile]="borderlessMobile"
      [dynamicValidation]="dynamicValidation"
      [control]="queryControl"
      [disabled]="disabled"
      [expanded]="expanded"
      [size]="inputSearchSize"
      [iconMarginMobile]="iconMarginMobile"
      [iconStart]="showIcon ? leftIcon : undefined"
      [iconStartLabel]="'Search'"
      [leftIconSize]="leftIconSize"
      [id]="inputId"
      [isExpandable]="isExpandable"
      [expandFromDirection]="expandFromDirection"
      [leftIconColor]="searchIconColor"
      [placeholder]="placeholder"
      [rightIconColor]="IconColor.FOREGROUND"
      [type]="InputType.SEARCH"
      [searchRoundCorners]="roundCorners"
      [shakeOnBlur]="shakeOnBlur"
      (iconStartClick)="handleIconStartClick($event)"
      (keyup)="handleInputKeyUp($event)"
      (keydown)="handleInputKeyDown($event)"
      (onFocus)="handleFocus()"
    ></fl-input>
    @if (queryControl.value && clearButton) {
      <fl-button
        class="ButtonClear"
        [disabled]="disabled"
        (click)="handleIconClearClick($event)"
      >
        <fl-icon
          class="IconClear"
          [name]="'ui-close'"
          [size]="IconSize.SMALL"
          [useIconFont]="true"
          [fillIconFont]="true"
        ></fl-icon>
      </fl-button>
    }

    @if (
      (displayResultsWithEmptyInput && !queryControl.value) ||
      (displayResults && queryControl.value)
    ) {
      <fl-card
        class="SearchResults"
        [borderRadius]="resultsBorderRadius"
        [edgeToEdge]="true"
        [class.SearchResults-callout]="increaseZIndex"
      >
        @if (scrollableSearchResults) {
          <fl-scrollable-content
            class="ScrollbarContainer"
            [autoHeight]="true"
          >
            <ng-container *ngTemplateOutlet="searchResults"></ng-container>
          </fl-scrollable-content>
        } @else {
          <ng-container *ngTemplateOutlet="searchResults"></ng-container>
        }
      </fl-card>
    }

    <ng-template #searchResults>
      @for (type of itemTypes; track trackByValue($index, type)) {
        <fl-list>
          @if (itemTypeTitles[type]) {
            <fl-list-item>
              <p class="text-xxxsmall font-bold uppercase">
                {{ itemTypeTitles[type] }}
              </p>
            </fl-list-item>
          }
        </fl-list>
        <fl-list
          [bottomBorder]="true"
          [clickable]="true"
        >
          @for (
            item of groupedSearchResults[type];
            track trackBySearchItem(i, item);
            let i = $index
          ) {
            <fl-list-item
              #listItem
              [showAsSelected]="focusIndex === groupOffsets[type] + i"
              (mousemove)="mouseMove(listItem)"
              (mouseleave)="mouseLeave()"
              (click)="selectItem(item)"
              [attr.itemType]="type"
              [attr.itemIndex]="i"
            >
              <ng-container
                *ngTemplateOutlet="
                  itemTemplates[item.type];
                  context: { $implicit: item.context }
                "
              ></ng-container>
            </fl-list-item>
          }
        </fl-list>
      }
    </ng-template>
  `,
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent implements OnChanges, OnInit, OnDestroy {
  private focus = inject(Focus);
  private cdr = inject(ChangeDetectorRef);
  private timeUtils = inject(TimeUtils);

  IconSize = IconSize;
  IconColor = IconColor;
  InputType = InputType;
  expanded = false;
  groupedSearchResults: GroupedSearchResults = {};
  queryControl = new FormControl('', { nonNullable: true });
  focusIndex = -1;
  focusItem?: ListItemComponent<unknown>;
  itemTypes: readonly string[] = [];
  groupOffsets: { [type: string]: number } = {};
  searchIconColor: IconColor;
  trackByValue = trackByValue;

  @Input() disabled = false;
  @Input() isExpandable = false;
  /**
   * Determines the direction from which the search component expands.
   */
  @Input() expandFromDirection = SearchExpandFromDirection.RIGHT;
  @Input() inputSearchSize = InputSize.MID;
  @Input() leftIconSize = IconSize.MID;
  @Input() itemTypeTitles: { [type: string]: string } = {};
  @Input() itemTemplates: { [type: string]: TemplateRef<any> } = {};
  /** Limits search results' container height and makes it scrollable. */
  @Input() scrollableSearchResults = false;
  /** Max height of the scrollable results container in pixels. */
  @Input() scrollableResultsMaxHeight?: number;

  @HostBinding('style.--scrollable-results-max-height')
  get scrollableResultsMaxHeightStyle(): string | null {
    return this.scrollableResultsMaxHeight
      ? `${this.scrollableResultsMaxHeight}px`
      : null;
  }
  @Input() searchResults: readonly SearchItem[] = [];
  @Input() placeholder = $localize`Search`;
  @Input() displayResults = true;
  @Input() initialValue = '';
  /** HTML id for the input element. */
  @Input() inputId?: string;
  /**
   * Show options when the user has not entered anything
   */
  @Input() displayResultsWithEmptyInput = false;
  /**
   * Increase the result card z-index to callout level. Default: dropdown.
   */
  @Input() increaseZIndex = false;
  /** Border radius of the results card. */
  @Input() resultsBorderRadius: CardBorderRadius = 'mid';
  /**
   * Not for general use. This is only for Messaging search.
   * If you'd like to use this input, make sure you discuss with UI Eng
   */
  @Input() borderless = false;
  /**
   * Not for general use. This is only for browse mobile page.
   * If you'd like to use this input, make sure you discuss with UI Eng
   */
  @Input() borderlessMobile = false;
  /**
   * Not for general use. This is only for browse mobile page.
   * If you'd like to use this input, make sure you discuss with UI Eng.
   */
  @Input() iconMarginMobile = true;
  @Input() expandableIconLight = false;
  @Input() clearQueryOnSelect = false;

  @Input() showIcon = true;
  @Input() leftIcon: InputIconStart = 'ui-search';

  @Input() roundCorners = true;
  @Input() backgroundColor: InputBackgroundColor = 'default';

  // Toggles the clear button
  @Input() clearButton = true;

  // Additional togglable fields for fl-input
  @Input() dynamicValidation = false;
  @Input() shakeOnBlur = true;

  // Custom control for use-cases that needs input suggestion
  @Input() control?: FormControl<string>;

  /**
   * Debounces the emission of the search query after the specified number of milliseconds.
   */
  @Input() debounceTimeMs?: number;

  @Output() query = new EventEmitter<string>();
  @Output() select = new EventEmitter<SearchItem>();
  @Output() submit = new EventEmitter<void>();
  @Output() clear = new EventEmitter<void>();
  @Output() onFocus = new EventEmitter<void>();

  // Reference to input component
  @ViewChild('inputComponent')
  inputComponent: InputComponent<any>;
  // Reference to list of components
  @ViewChildren('listItem') listItems: QueryList<ListItemComponent<unknown>>;
  // Reference to list of ElementRef<HTMLElement>s
  @ViewChildren('listItem', { read: ElementRef })
  listItemElements: QueryList<ElementRef<HTMLElement>>;

  private querySubscription?: Subscription;

  // Listening for clicks outside the input component
  @HostListener('document:click', ['$event'])
  documentClick(event: MouseEvent): void {
    if (
      event &&
      !this.inputComponent.inputContainer.nativeElement.contains(
        event.target as HTMLElement,
      ) &&
      this.expanded
    ) {
      // expanded while there is a query
      this.expanded = !!this.queryControl.value;
      this.displayResults = false;
      this.setSearchIconColor();
    }
  }

  ngOnInit(): void {
    this.subscribeToQueryControl();
    this.setSearchIconColor();
  }

  setSearchIconColor(): void {
    if (!this.isExpandable || this.expanded) {
      this.searchIconColor = IconColor.FOREGROUND;
      return;
    }

    if (this.expandableIconLight) {
      this.searchIconColor = IconColor.LIGHT;
    }
  }

  subscribeToQueryControl(): void {
    this.queryControl = this.control ?? this.queryControl;
    this.querySubscription?.unsubscribe();
    this.querySubscription = this.queryControl.valueChanges
      .pipe(
        this.debounceTimeMs
          ? this.timeUtils.rxDebounceTime(this.debounceTimeMs)
          : query$ => query$,
      )
      .subscribe(query => {
        this.query.emit(query);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.removeFocus();

    if ('debounceTimeMs' in changes) {
      this.subscribeToQueryControl();
    }

    const baseGroupedSearchResults: GroupedSearchResults = { untyped: [] };
    this.groupedSearchResults = this.searchResults.reduce((acc, i) => {
      if (!(i.type in acc)) {
        acc[i.type] = [];
      }
      acc[i.type].push(i);
      return acc;
    }, baseGroupedSearchResults);

    this.itemTypes = Object.keys(this.groupedSearchResults);

    let offset = 0;
    this.groupOffsets = {};
    for (const type of this.itemTypes) {
      this.groupOffsets[type] = offset;
      offset += this.groupedSearchResults[type].length;
    }

    if (this.clearQueryOnSelect) {
      this.clearQuery();
    }

    if ('initialValue' in changes && this.initialValue !== '') {
      this.queryControl.setValue(this.initialValue);
    }
  }

  ngOnDestroy(): void {
    this.querySubscription?.unsubscribe();
  }

  handleIconStartClick(e: MouseEvent): void {
    e.stopPropagation();
    this.focus.focusElement(this.inputComponent.nativeElement);
    this.displayResults = true;
    this.expanded = true;
    this.setSearchIconColor();
  }

  handleIconClearClick(e: MouseEvent): void {
    e.stopPropagation();
    this.clearQuery();
    this.focus.focusElement(this.inputComponent.nativeElement);
  }

  handleFocus(): void {
    this.onFocus.emit();
  }

  clearQuery(): void {
    this.queryControl.setValue('');
    this.clear.emit();
  }

  handleInputKeyDown(e: KeyboardEvent): void {
    if (!e.key) return;

    const k = e.key.toLowerCase();
    switch (k) {
      case 'arrowdown': {
        // Prevent cursor from moving to the right
        e.preventDefault();
        break;
      }
      case 'arrowup': {
        // Prevent cursor from moving to the left
        e.preventDefault();
        break;
      }
      case 'tab': {
        // Prevent default tab action
        e.preventDefault();
        break;
      }
      default:
        break; // Do nothing
    }
  }

  handleInputKeyUp(e: KeyboardEvent): void {
    if (!e.key) return;

    const k = e.key.toLowerCase();
    if (k === 'enter') {
      this.selectOrSubmit();
      return;
    }
    switch (k) {
      case 'escape': {
        // IMPORTANT - if you modify this behaviour make sure
        // to update multi-select. They should offer the same functionality.
        this.clearQuery();
        break;
      }
      case 'arrowdown': {
        this.moveDown();
        e.stopPropagation();
        e.preventDefault();
        break;
      }
      case 'arrowup': {
        this.moveUp();
        e.stopPropagation();
        e.preventDefault();
        break;
      }
      case 'tab': {
        if (e.shiftKey) {
          this.moveUp();
        } else {
          this.moveDown();
        }
        e.stopPropagation();
        e.preventDefault();
        break;
      }
      default:
        break; // Do nothing
    }
  }

  selectOrSubmit(): void {
    if (this.focusItem) {
      const itemType = this.listItemElements
        .toArray()
        [this.focusIndex].nativeElement.getAttribute('itemType') as string;
      const itemIndex = Number(
        this.listItemElements
          .toArray()
          [this.focusIndex].nativeElement.getAttribute('itemIndex') as string,
      );
      this.select.emit(this.groupedSearchResults[itemType][itemIndex]);
    } else {
      this.submit.emit();
    }
  }

  selectItem(item: SearchItem): void {
    this.select.emit(item);
    if (item.displayValue) {
      this.queryControl.setValue(item.displayValue, {
        emitEvent: false,
      });
    } else {
      this.clearQuery();
    }
  }

  // Focus helpers - there's quite a few of these, sorry.
  moveDown(): void {
    this.focusIndex += 1;
    this.reFocus();
  }

  moveUp(): void {
    this.focusIndex -= 1;
    this.reFocus();
  }

  mouseMove(item: ListItemComponent<unknown>): void {
    this.focusIndex = this.listItems.toArray().indexOf(item);
    this.reFocus();
  }

  mouseLeave(): void {
    this.removeFocus();
  }

  reFocus(): void {
    this.fixFocusOverflow();
    this.updateFocusItem();
    this.scrollFocusedItemIntoView();
  }

  updateFocusItem(): void {
    const focusItem = this.listItems.toArray()[this.focusIndex];
    if (focusItem) {
      this.focusItem = focusItem;
    }
    this.cdr.markForCheck();
  }

  removeFocus(): void {
    if (this.focusItem) {
      delete this.focusItem;
      this.focusIndex = -1;
      this.cdr.markForCheck();
    }
  }

  fixFocusOverflow(): void {
    if (this.focusIndex < 0) {
      this.focusIndex = this.searchResults.length - 1;
    } else if (this.focusIndex >= this.searchResults.length) {
      this.focusIndex = 0;
    }
  }

  scrollFocusedItemIntoView(): void {
    const element = this.listItemElements?.toArray()[this.focusIndex];
    if (element) {
      element.nativeElement.scrollIntoView({ block: 'nearest' });
    }
  }

  trackBySearchItem(_: number, item: SearchItem): SearchItem {
    return item;
  }

  // T252371 - Set the query value on selection of an item in
  // a custom (external) search results list
  setQuery(query: string): void {
    this.queryControl.setValue(query);
  }
}
